import { inject, InjectionToken } from '@angular/core';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { catchError, map, take } from 'rxjs/operators';
import { Company, CompanyStateEnum, UserGQL } from '@app/generated/graphql';
import { Observable, of } from 'rxjs';
import * as Sentry from '@sentry/browser';

// need a token to inject Sentry into the guard so we can mock it in tests
// this is necessary since guard functions don't support constructor injection
export const SENTRY_TOKEN = new InjectionToken<typeof Sentry>('SentryBrowser', {
  providedIn: 'root',
  factory: () => Sentry,
});

const statesForSeasonSetup: CompanyStateEnum[] = [
  CompanyStateEnum.Onboarding,
  CompanyStateEnum.ReOnboarding,
];

export const onboardingGuard: CanActivateFn = (
  _route,
  _state,
): Observable<boolean | UrlTree> => {
  // dependencies
  const router = inject(Router);
  const userGql = inject(UserGQL);
  const sentry = inject(SENTRY_TOKEN);

  /**
   * Fetch the user's company and check if it is in the onboarding or re-onboarding state.
   * If it is, allow access to the season setup page.
   * If it is not, redirect to the account page.
   */
  return userGql.fetch().pipe(
    take(1),
    map((res) => res.data.myCompany),
    map(
      (company: Company | null) =>
        !!company?.state && statesForSeasonSetup.includes(company.state),
    ),
    map((isAllowed: boolean) =>
      isAllowed ? true : router.createUrlTree(['/account']),
    ),
    catchError((error) => {
      console.error('OnboardingGuard - Error:', error);
      sentry.captureException(error);

      // redirect to the account page if there is an error
      return of(router.createUrlTree(['/account']));
    }),
  );
};
